/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import error from '../../../../../common/nodejs/errors';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import config from '../../../../../share/config';
import session from '../../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',//実行監視
  SUCCESS: 'success',//成功表示
  TIMEOUT: 'timeout',//タイムアウト
  FAILED: 'failed'//共通エラー
};

export default {
  name: 'requirementSpES003',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      isInitialized: true,
      isErrorCheck: true,
      modal_special_requirement_ES3: false
    };
  },

  props: {
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.TIMEOUT:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_special_requirement_ES3 = true;
    },

    /**
     * 終了処理
     */
    async exitProcess() {
      if (this.isInitialized) {
        // 終了処理を行う際にterminateを一度だけ呼ぶ
        this.isInitialized = false;
        await this.exitLogic();
      } else {
        // Wizardを閉じる
        this.closeWizard();
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_special_requirement_ES3 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 終了処理（要求） */
    async exitLogic() {
      try {
        // スピナー表示
        this.isLoading = true;

        // EUCキーの取得
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        // 終了処理 (要求)（バックエンドAPI_POST） Getのポーリング処理も行う
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'terminate',
            is_special: true
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // Wizardを閉じる
              this.closeWizard();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              //エラー画面表示済みの場合はそのまま機能終了する
              if (this.isErrorCheck) {
                this.showUnexpectedError();
              } else {
                this.closeWizard();
              }
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** ルーチン実行処理 */
    async execute() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        // データ読み出し(要求)（バックエンドAPI_POST） Getのポーリング処理も行う
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby',
            is_special: true
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：読み出しデータ表示
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              if (response.data.async_code === error.WSP_CONDITION_FAILURE_TIMEOUT_ERROR) {
                //タイムアウト処理
                await this.stopExecute();
                this.phase = PHASE.TIMEOUT;
              } else if (response.data.async_code === error.WSP_CONDITION_FAILURE_ERROR) {
                //実行監視で失敗
                await this.stopExecute();
                this.showUnexpectedError();
              } else {
                this.showUnexpectedError();
              }
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      this.isErrorCheck = false;
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /** ルーチン停止(応答不要) */
    async stopExecute() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        // モック対応
        systemId;
        token;
        // ルーチン停止（バックエンドAPI_POST） Getのポーリング処理も行う
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'stop',
            is_special: true
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        //ルーチン停止は結果を確認しない
      } finally {
        this.isLoading = false;
      }
    },

    /** 成功表示 OK*/
    async successProcess() {
      await this.stopExecute();
      this.exitProcess();
    },
  },

  async mounted() {
    this.init();
    // ルーチン実行
    this.execute();
  },
};